import { useState, useEffect, useCallback, useRef } from 'react';

import { readStationAutomationConfig, writeStationAutomationConfig } from './stationAutomationConfigApi';
import { stationFlowsToAutomationConfig, applyAutomationConfigToStationFlows } from './FlowPlannerModel';

// Station automation config, used only before a plan exists. `exists` follows the
// mapped config, so an unmappable file counts as no file.
export default function useStationAutomationConfig(enabled) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The full file, kept so a save preserves keys we don't edit (cycleExecutionFlows).
  const fileRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setConfig(null);
      fileRef.current = null;
      return;
    }
    // Per-run flag: `enabled` can flip false mid-read, and a late resolve would
    // undo the clearing above.
    let cancelled = false;
    readStationAutomationConfig()
      .then((file) => {
        if (cancelled) return;
        fileRef.current = file;
        setConfig(stationFlowsToAutomationConfig(file?.executionFlows));
      })
      .catch((e) => {
        console.warn('Flow Planner — failed to read station AUTOMATION_CONFIG:', e);
        if (cancelled) return;
        fileRef.current = null;
        setConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Discards edits by re-deriving from the file we last read.
  const reset = useCallback(() => {
    setError(null);
    setConfig(stationFlowsToAutomationConfig(fileRef.current?.executionFlows));
  }, []);

  const save = useCallback(async () => {
    if (!fileRef.current || !config) return false;
    setSaving(true);
    setError(null);
    try {
      const file = JSON.parse(JSON.stringify(fileRef.current));
      applyAutomationConfigToStationFlows(file.executionFlows, config);
      await writeStationAutomationConfig(file);
      if (aliveRef.current) fileRef.current = file;
      return true;
    } catch (e) {
      if (aliveRef.current) setError(e.message || 'Failed to save the automation config.');
      return false;
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  }, [config]);

  return { exists: !!config, config, setConfig, save, reset, saving, error };
}
